import React from 'react';
import styled from 'styled-components';
import { CompressedImage } from 'web-services/Imgix';
import { ProductCardReferrer } from 'web-ui-kit/cards/ProductCard';
import { productLikeStatusChanged } from 'web-ui-kit/cards/ProductCardEvents';
import { surfaceM } from 'web-ui-kit/colors';
import { useImageLoading } from 'web-ui-kit/hooks';
import LikeDislikeButtons from 'web-ui-kit/LikeDislikeButtons';
import Tag from 'web-ui-kit/Tag';
import SkeletonBone from 'web-ui-kit/loaders/SkeletonBone';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;
/**
 *  We don't want to show certain badges that are present on the Product model.
 */


var BLACKLISTED_BADGES = ['Modsy Choice'];
/**
 * A 1x1 product image with optional product like/dislike control and badge.
 */

function ProductImage(_ref) {
  var _curatedRec$label, _curatedRec$recommend, _curatedRec$recommend2, _curatedRec$recommend3, _curatedRec$recommend4;

  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$dislikeButtonCon = _ref.dislikeButtonConfig,
      dislikeButtonConfig = _ref$dislikeButtonCon === void 0 ? 'hover' : _ref$dislikeButtonCon,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$image = _ref.image,
      image = _ref$image === void 0 ? '' : _ref$image,
      _ref$likeDislikeConfi = _ref.likeDislikeConfig,
      likeDislikeConfig = _ref$likeDislikeConfi === void 0 ? {
    enabled: true
  } : _ref$likeDislikeConfi,
      _ref$loading = _ref.loading,
      loading = _ref$loading === void 0 ? undefined : _ref$loading,
      _ref$mqTwoColumnUp = _ref.mqTwoColumnUp,
      mqTwoColumnUp = _ref$mqTwoColumnUp === void 0 ? '' : _ref$mqTwoColumnUp,
      _ref$parentProductId = _ref.parentProductId,
      parentProductId = _ref$parentProductId === void 0 ? undefined : _ref$parentProductId,
      _ref$product = _ref.product,
      product = _ref$product === void 0 ? {} : _ref$product,
      _ref$setProductImageE = _ref.setProductImageEl,
      setProductImageEl = _ref$setProductImageE === void 0 ? function () {} : _ref$setProductImageE;

  var _useImageLoading = useImageLoading({
    image: image
  }),
      handleImageLoaded = _useImageLoading.handleImageLoaded,
      showLoader = _useImageLoading.showLoader;

  var _product$curatedRec = product.curatedRec,
      curatedRec = _product$curatedRec === void 0 ? {} : _product$curatedRec,
      _product$badge = product.badge,
      badge = _product$badge === void 0 ? '' : _product$badge,
      _product$id = product.id,
      id = _product$id === void 0 ? '' : _product$id,
      _product$liked = product.liked,
      liked = _product$liked === void 0 ? false : _product$liked,
      _product$name = product.name,
      name = _product$name === void 0 ? '' : _product$name;
  var badgeType = (_curatedRec$label = curatedRec === null || curatedRec === void 0 ? void 0 : curatedRec.label) !== null && _curatedRec$label !== void 0 ? _curatedRec$label : !BLACKLISTED_BADGES.includes(badge) && badge;
  var badgeCategory = curatedRec !== null && curatedRec !== void 0 && curatedRec.label ? 'Curated' : '';
  var badgeImageData = {
    alt: (_curatedRec$recommend = curatedRec === null || curatedRec === void 0 ? void 0 : (_curatedRec$recommend2 = curatedRec.recommender) === null || _curatedRec$recommend2 === void 0 ? void 0 : _curatedRec$recommend2.firstName) !== null && _curatedRec$recommend !== void 0 ? _curatedRec$recommend : '',
    imageSrc: (_curatedRec$recommend3 = curatedRec === null || curatedRec === void 0 ? void 0 : (_curatedRec$recommend4 = curatedRec.recommender) === null || _curatedRec$recommend4 === void 0 ? void 0 : _curatedRec$recommend4.avatarSrc) !== null && _curatedRec$recommend3 !== void 0 ? _curatedRec$recommend3 : ''
  };

  var handleLikeDislikeChange = function handleLikeDislikeChange(event) {
    dispatch(productLikeStatusChanged({
      event: event,
      parentProductId: parentProductId,
      productId: id,
      product: product,
      referrer: ProductCardReferrer.PDP
    }));
  };

  var Image = styles.Image,
      OneByOneAspectRatioContainer = styles.OneByOneAspectRatioContainer,
      ProductLikeDislikeButtons = styles.ProductLikeDislikeButtons,
      ProductTag = styles.ProductTag,
      SkeletonContainer = styles.SkeletonContainer;
  return (
    /*#__PURE__*/
    React.createElement(OneByOneAspectRatioContainer, {
      className: className,
      ref: setProductImageEl
    },
    /*#__PURE__*/
    React.createElement(Image, {
      key: image,
      alt: name,
      bucketName: "modsy",
      loading: loading,
      onLoad: handleImageLoaded,
      src: image,
      sizes: "".concat(mqTwoColumnUp.replace(/@media /, ''), " 512px, 100vw")
    }), likeDislikeConfig.enabled &&
    /*#__PURE__*/
    React.createElement(ProductLikeDislikeButtons, {
      dislikeButtonConfig: dislikeButtonConfig,
      dispatch: handleLikeDislikeChange,
      isDisliked: liked < 0,
      isLiked: liked > 0
    }), badgeType &&
    /*#__PURE__*/
    React.createElement(ProductTag, {
      category: badgeCategory,
      imageData: badgeImageData,
      size: "large",
      type: badgeType
    }), showLoader &&
    /*#__PURE__*/
    React.createElement(SkeletonContainer, null,
    /*#__PURE__*/
    React.createElement(SkeletonBone, {
      height: "100%"
    })))
  );
}

var styles = {
  OneByOneAspectRatioContainer: styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    height: 0;\n    padding-bottom: 100%;\n    width: 100%;\n\n    background-color: ", ";\n    position: relative;\n\n    /** Ensure non 1x1 product images are centered. */\n    display: flex;\n    align-items: center;\n    justify-content: center;\n  "])), surfaceM),
  Image: styled(CompressedImage)(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    position: absolute;\n    top: 0;\n    left: 0;\n    width: 100%;\n    height: 100%;\n    object-fit: contain;\n  "]))),
  ProductLikeDislikeButtons: styled(LikeDislikeButtons)(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    position: absolute;\n    top: 0.5rem;\n    right: 1rem;\n\n    filter: drop-shadow(0px 2px 4px rgba(65, 67, 74, 0.15));\n  "]))),
  ProductTag: styled(Tag)(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    position: absolute;\n    bottom: 0.5rem;\n    left: 1rem;\n  "]))),
  SkeletonContainer: styled.div(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    position: absolute;\n    top: 0;\n    left: 0;\n    width: 100%;\n    height: 100%;\n  "])))
};
export default ProductImage;